import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../auth/useAuth0';
import HeaderComponent from './HeaderComponent';
import BrandThemeApplier from './BrandThemeApplier';
import LoadingComponent from '../commons/LoadingComponent';
import { getBusinessFromLocalStorage, saveBusinessToLocalStorage } from '../../util/local-storage-service';
import { findAllBusinessSummaryByOwnerId } from '../../service/business/business-service';
import CalendarComponent from "../menu/calendar/CalendarComponent";
import AiHomeComponent from '../menu/ai/AiHomeComponent';
import AppointmentsComponent from "../menu/appointments/AppointmentsComponent";
import WaitlistComponent from "../menu/waitlist/WaitlistComponent";
import LeftPanelComponent from "./LeftPanelComponent";
import { MENU, MENU_ORDER, menuAllowed } from "../util/Utils";
import { usePlan } from '../../context/PlanContext';
import { usePermissions } from "../../context/PermissionsContext";
import SettingsPageComponent from "../menu/settings/SettingsPageComponent";
import ProfileComponent from "../menu/settings/profile/ProfileComponent";
import NotificationsComponent from "../menu/notifications/NotificationsComponent";
import PaymentsComponent from "../menu/payments/PaymentsComponent";
import ReportsComponent from "../menu/reports/ReportsComponent";
import ServicesComponent from "../menu/services/ServicesComponent";
import TeamMemberComponent from "../menu/teammember/TeamMemberComponent";
import ClientsComponent from "../menu/clients/ClientsComponent";
import PayrollComponent from "../menu/payroll/PayrollComponent";
import FinanceComponent from "../menu/finance/FinanceComponent";
import MessagesComponent from "../menu/messages/MessagesComponent";
import NoAccessComponent from "../menu/no-access/NoAccessComponent";
import LoadInitialData from "../../auth/LoadInitialData";
import {useUser} from "../../auth/UserContext";
import {useBusinessContext} from "../../context/BusinessContext";
import { useNavigation } from "../../context/NavigationContext";
import BusinessOnboardingWizard from "../onboarding/BusinessOnboardingWizard";
import { BusinessSummary } from "../../model/business/BusinessSummary";


const selectedMenuItemByDefault = MENU.CALENDAR;

const HomeComponent: React.FC = () => {
    const { t } = useTranslation();
    const { activeMenu: activeComponent, setActiveMenu: setActiveComponent } = useNavigation();
    const { user } = useUser();
    const { logout, getAccessTokenSilently } = useAuth0();
    const logoutWithRedirect = () =>
        logout({ logoutParams: { returnTo: window.location.origin } });
    const { isBusinessLoading, allBusiness, selectedBusiness, setSelectedBusiness, setAllBusiness } = useBusinessContext();
    const { grants, canView } = usePermissions();
    const { hasFeature } = usePlan();

    // True once permissions have resolved AND the caller can't view a single
    // left-nav screen. A zero-permission member must NEVER silently land on the
    // Calendar (or any screen) — we render a dedicated dead-end instead. While
    // grants are null (loading / fetch failed) canView is fail-open, so this
    // stays false and the normal shell renders.
    const noMenuAccess = !!grants && !MENU_ORDER.some((k) => menuAllowed(k, canView));

    // Keep the user off screens they can't access. If the active screen (e.g.
    // restored from a refresh, or the default Calendar) is gated by a permission
    // the caller lacks, send them to the first screen they CAN see. Waits for
    // permissions to load (grants !== null) so admins/owners aren't bounced.
    // When the caller has access to NOTHING there is no screen to send them to —
    // noMenuAccess takes over the content area below.
    // The assistant screen is gone when the plan has no AI — land on the Calendar
    // instead of an empty shell. hasFeature is fail-open while the plan loads, so
    // this never flashes for a business that does have it.
    useEffect(() => {
        if (activeComponent === MENU.HOME && !hasFeature('AI')) {
            setActiveComponent(MENU.CALENDAR);
        }
    }, [activeComponent, hasFeature, setActiveComponent]);

    useEffect(() => {
        if (!grants) return;
        if (noMenuAccess) return;
        if (!menuAllowed(activeComponent, canView)) {
            const firstAllowed = MENU_ORDER.find((k) => menuAllowed(k, canView));
            if (firstAllowed && firstAllowed !== activeComponent) {
                setActiveComponent(firstAllowed);
            }
        }
    }, [grants, noMenuAccess, activeComponent, canView, setActiveComponent]);

    // Onboarding wizard. Opens automatically for the "create intent" (marketing
    // "Create your business" lands on /home?create=1), or manually from the
    // no-business screen. The create intent opens it even when the user already
    // has businesses (they explicitly chose to add a new one).
    const [showWizard, setShowWizard] = useState<boolean>(() => {
        const param = new URLSearchParams(window.location.search).get('create') === '1';
        let stashed = false;
        try { stashed = sessionStorage.getItem('gs_create_business') === '1'; } catch { /* ignore */ }
        return param || stashed;
    });

    const clearCreateIntent = () => {
        try { sessionStorage.removeItem('gs_create_business'); } catch { /* ignore */ }
        // Drop the ?create intent so a refresh doesn't reopen the wizard.
        window.history.replaceState({}, '', window.location.pathname);
    };

    const handleWizardDone = (created: BusinessSummary) => {
        setAllBusiness([...(allBusiness ?? []), created] as any);
        setSelectedBusiness(created as any);
        setShowWizard(false);
        clearCreateIntent();
    };

    // Pick the initial business as soon as the list arrives — runs even while
    // the shell is gated behind the loader (HeaderComponent isn't mounted yet),
    // so branding resolves before we reveal the UI.
    useEffect(() => {
        if (!allBusiness || allBusiness.length === 0 || selectedBusiness) return;
        const cached = getBusinessFromLocalStorage();
        const fresh = cached?.id
            ? allBusiness.find((b) => String(b.id) === String(cached.id))
            : null;
        // Only ever select a business that's actually in THIS user's accessible
        // list. The cached value can be stale (a previous user/session in the
        // same browser); falling back to it would load another business's data.
        const chosen = fresh ?? allBusiness[0] ?? null;
        setSelectedBusiness(chosen);
        // Persist the default too (not only combo switches) so an F5 deterministically
        // restores the SAME business instead of resolving to a stale cache / list[0].
        saveBusinessToLocalStorage(chosen);
    }, [allBusiness, selectedBusiness, setSelectedBusiness]);

    // Re-validate access while the session is open: an owner can delete a team
    // member (or revoke app access) in another tab. We re-fetch the accessible
    // businesses periodically and on tab focus; the list is derived from the
    // JWT on the backend, so a revoked user gets an empty/smaller list. The
    // reconcile effect below then drops the lost business (→ "No access" screen
    // when nothing is left).
    useEffect(() => {
        if (!user?.id) return;
        let cancelled = false;
        const revalidate = async () => {
            try {
                const token = await getAccessTokenSilently();
                const fresh = await findAllBusinessSummaryByOwnerId(token);
                if (!cancelled) setAllBusiness(fresh);
            } catch { /* transient — try again next tick */ }
        };
        const intervalId = window.setInterval(revalidate, 60_000);
        const onVisible = () => {
            if (document.visibilityState === 'visible') revalidate();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user?.id, getAccessTokenSilently, setAllBusiness]);

    // Reconcile the selected business against the (possibly refreshed) list.
    // If the current one is gone, fall back to the first still-accessible one,
    // or null — which trips the "No business access" gate below.
    useEffect(() => {
        if (allBusiness === null || !selectedBusiness) return;
        const stillThere = allBusiness.some(
            (b) => String(b.id) === String(selectedBusiness.id)
        );
        if (!stillThere) {
            const next = allBusiness[0] ?? null;
            setSelectedBusiness(next);
            saveBusinessToLocalStorage(next); // keep cache in sync (null clears it)
        }
    }, [allBusiness, selectedBusiness, setSelectedBusiness]);

    // The shell is revealed only once branding is resolved, so the user never
    // sees the default theme flash before the business colors load:
    //   - business list still loading       → wait
    //   - has businesses but none selected   → wait (the effect above is about
    //                                           to set it + apply branding)
    //   - no businesses at all               → nothing to theme, reveal
    const businessesLoaded = allBusiness !== null;
    const hasBusinesses = (allBusiness?.length ?? 0) > 0;
    // The app shell only mounts once a business is resolved. A user with zero
    // accessible businesses (not an owner, not a team member anywhere) gets a
    // clear "no access" screen instead of an empty, business-less UI that would
    // otherwise issue unscoped/empty queries.
    const appReady = businessesLoaded && !isBusinessLoading && hasBusinesses && !!selectedBusiness;
    const noAccess = businessesLoaded && !isBusinessLoading && !hasBusinesses;

    const renderActiveComponent = () => {
        switch (activeComponent) {
            case MENU.HOME:
                return <AiHomeComponent />;
            case MENU.CALENDAR:
                return <CalendarComponent />;
            case MENU.APPOINTMENTS:
                return <AppointmentsComponent />;
            case MENU.WAITLIST:
                return <WaitlistComponent />;
            case MENU.CLIENT:
                return (<ClientsComponent />);
            case MENU.SERVICES:
                return (<ServicesComponent />);
            case MENU.TEAM_MEMBER:
                return (<TeamMemberComponent />);
            case MENU.CHATS:
                return (<MessagesComponent />);
            case MENU.PAYMENTS:
                return (<PaymentsComponent />);
            case MENU.PAYROLL:
                return (<PayrollComponent />);
            case MENU.REPORTS:
                return (<ReportsComponent />);
            case MENU.FINANCE:
                return (<FinanceComponent />);
            case MENU.SETTINGS:
                return (<SettingsPageComponent />);
            case MENU.PROFILE:
                return (<ProfileComponent />);
            case MENU.NOTIFICATIONS:
                return (<NotificationsComponent />);
            default:
                return <div>{t('home.selectOption')}</div>;
        }
    };

    return (
        <>
            {/* Headless — must keep running while the shell is gated so the
                data + branding actually load. */}
            {!user?.id && !allBusiness && !isBusinessLoading && <LoadInitialData />}
            <BrandThemeApplier />

            {showWizard ? (
                <BusinessOnboardingWizard
                    onDone={handleWizardDone}
                    onCancel={() => {
                        setShowWizard(false);
                        clearCreateIntent();
                    }}
                    allowCancel={hasBusinesses}
                />
            ) : noAccess ? (
                // User resolved but belongs to no business — offer the wizard to
                // create one, or logout. (We don't render the business-less shell.)
                <div
                    data-cy="home-no-business-page"
                    style={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        backgroundColor: 'var(--gs-bg-app, #f2f2f5)',
                        textAlign: 'center',
                        padding: '2rem',
                    }}
                >
                    <h2 style={{ margin: 0, color: '#2a2245' }}>{t('home.noBusinessTitle')}</h2>
                    <p style={{ maxWidth: 420, color: '#6c757d' }}>
                        {t('home.noBusinessHint')}
                    </p>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowWizard(true)}
                            data-cy="home-create-business-btn"
                        >
                            {t('home.createBusiness')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={logoutWithRedirect}
                            data-cy="home-logout-btn"
                        >
                            {t('header.logout')}
                        </button>
                    </div>
                </div>
            ) : !appReady ? (
                // Hold a clean full-screen loader until branding is resolved —
                // no flash of the default theme before the business colors apply.
                <div
                    style={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--gs-bg-app, #f2f2f5)',
                    }}
                >
                    <LoadingComponent />
                </div>
            ) : (
                <div className="container-fluid custom-container d-flex" data-cy="home-shell">
                    {/* Menu lateral */}
                    <LeftPanelComponent setActiveComponent={setActiveComponent} selected={activeComponent}/>


                    {/* Área de conteúdo principal */}
                    <main className="flex-grow-1 ms-sm-auto col-lg-10 px-md-4 d-flex flex-column"
                          style={{backgroundColor: 'var(--gs-bg-app, #f2f2f5)', paddingTop: '20px'}}>
                        {/* Header dentro da área de conteúdo principal */}
                        <HeaderComponent/>

                        {/* Conteúdo dinâmico baseado na seleção do menu. A member
                            with zero permissions gets the dead-end fallback instead
                            of any functional screen. */}
                        <div className="content mt-3" data-cy="home-active-content">
                            {noMenuAccess ? <NoAccessComponent /> : renderActiveComponent()}
                        </div>
                    </main>
                </div>
            )}
        </>
    );
};

export default HomeComponent;